import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowRight, CheckCircle, Building2, Wrench, HardHat, 
  Factory, Truck, Mountain, Users, Clock, Shield, 
  Target, Handshake, Zap, FileText, Search, UserCheck
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import SEO from '@/components/SEO';

const HERO_BG = "https://images.unsplash.com/photo-1672954766776-cf2d157222b3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBjb25zdHJ1Y3Rpb24lMjBzaXRlJTIwcHJvZmVzc2lvbmFsJTIwd29ya2VycyUyMGF1c3RyYWxpYXxlbnwwfHx8fDE3NzM1MzkwMDV8MA&ixlib=rb-4.1.0&q=85";

const trustPoints = [
  { icon: Clock, text: 'Fast Shortlist Turnaround' },
  { icon: Shield, text: 'Better Screened Candidates' },
  { icon: Target, text: 'Construction Market Knowledge' },
  { icon: Handshake, text: 'Practical Recruitment Support' },
];

const sectors = [
  { icon: Building2, name: 'Construction', desc: 'Commercial, residential and civil construction roles' },
  { icon: HardHat, name: 'Engineering', desc: 'Civil, structural, mechanical and project engineers' },
  { icon: Wrench, name: 'Trades', desc: 'Electricians, plumbers, carpenters and more' },
  { icon: Factory, name: 'Industrial', desc: 'Manufacturing, warehouse and production staff' },
  { icon: Truck, name: 'Infrastructure', desc: 'Roads, rail, utilities and major projects' },
  { icon: Mountain, name: 'Mining Support', desc: 'Site services and mining industry roles' },
];

const employerServices = [
  { title: 'Permanent Recruitment', desc: 'Find the right people for long-term roles across your business.' },
  { title: 'Contract & Temporary', desc: 'Flexible workforce solutions for project demands.' },
  { title: 'Trade & Blue-Collar', desc: 'Skilled tradespeople and site-ready workers.' },
  { title: 'Engineering & Technical', desc: 'Qualified engineers and technical specialists.' },
  { title: 'Site & Project Support', desc: 'Coordinators, administrators and support staff.' },
  { title: 'Candidate Screening', desc: 'Thorough vetting and reference checking.' },
];

const whyVulcan = [
  { icon: Target, title: 'Industry-Aligned', desc: 'We understand construction and engineering hiring.' },
  { icon: Zap, title: 'Responsive Service', desc: 'Fast communication and quick turnaround times.' },
  { icon: Shield, title: 'Quality-First Screening', desc: 'Thorough candidate vetting every time.' },
  { icon: Clock, title: 'Urgent Hiring Support', desc: 'We move fast when you need people quickly.' },
  { icon: Users, title: 'Flexible Solutions', desc: 'Support that scales with your business.' },
  { icon: Handshake, title: 'Long-Term Partnerships', desc: 'Building relationships, not just filling roles.' },
];

const processSteps = [
  { num: '01', title: 'Brief', desc: 'Share your hiring needs and requirements with our team.' },
  { num: '02', title: 'Search', desc: 'We activate our network and source qualified candidates.' },
  { num: '03', title: 'Shortlist', desc: 'Receive screened candidates matched to your criteria.' },
  { num: '04', title: 'Placement', desc: 'Interview, select and onboard your new team member.' },
];

const testimonials = [
  { 
    quote: "Vulcan Recruitment understood exactly what we needed for our site team. Fast, professional and the candidates were spot-on.",
    name: "Michael Chen",
    role: "Operations Manager",
    company: "BuildCo Victoria"
  },
  { 
    quote: "Finally a recruiter that actually knows construction. No time wasted on unsuitable candidates.",
    name: "Sarah Mitchell",
    role: "HR Director",
    company: "Infrastructure Partners"
  },
  { 
    quote: "Their responsiveness is exceptional. When we need people urgently, they deliver.",
    name: "David Thompson",
    role: "Project Director",
    company: "Civil Works Group"
  },
];

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
};

export default function Home() {
  return (
    <div data-testid="home-page">
      <SEO page="home" />
      {/* Hero Section */}
      <section 
        data-testid="hero-section"
        className="relative min-h-screen flex items-center pt-28"
      >
        <div className="absolute inset-0 z-0">
          <img 
            src={HERO_BG} 
            alt="Construction site" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0F172A]/95 via-[#0F172A]/80 to-[#0F172A]/40" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-20">
          <div className="max-w-3xl">
            <motion.p 
              {...fadeInUp}
              className="text-[#F5A623] font-semibold uppercase tracking-wider mb-4"
            >
              Specialist Recruitment Agency
            </motion.p>
            <motion.h1 
              {...fadeInUp}
              transition={{ delay: 0.1, duration: 0.6 }}
              className="font-heading text-4xl sm:text-5xl lg:text-7xl font-bold text-white uppercase leading-tight mb-6"
            >
              Construction & Engineering Recruitment Across Australia
            </motion.h1>
            <motion.p 
              {...fadeInUp}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="text-lg md:text-xl text-slate-300 mb-8 leading-relaxed"
            >
              Connecting businesses with skilled professionals across construction, engineering, trades, industrial and infrastructure roles.
            </motion.p>
            <motion.div 
              {...fadeInUp}
              transition={{ delay: 0.3, duration: 0.6 }}
              className="flex flex-col sm:flex-row gap-4"
            >
              <Link to="/employers#request-staff">
                <Button 
                  data-testid="hero-request-staff"
                  className="bg-[#1A8B9D] hover:bg-[#115E6B] text-white font-bold py-6 px-8 uppercase tracking-wide text-base"
                >
                  Request Staff <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/candidates">
                <Button 
                  data-testid="hero-submit-cv"
                  className="bg-[#F5A623] hover:bg-[#D98E15] text-slate-900 font-bold py-6 px-8 uppercase tracking-wide text-base"
                >
                  Submit CV
                </Button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Trust Bar */}
      <section className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            {trustPoints.map((point, idx) => (
              <motion.div 
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="flex items-center gap-3"
              >
                <point.icon className="w-6 h-6 text-[#1A8B9D] flex-shrink-0" />
                <span className="text-sm md:text-base font-semibold text-slate-700">{point.text}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* About Preview */}
      <section data-testid="about-preview" className="py-20 md:py-28 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <p className="text-[#F5A623] font-semibold uppercase tracking-wider mb-3">About Us</p>
              <h2 className="font-heading text-3xl md:text-5xl font-bold text-slate-900 uppercase mb-6">
                Recruitment That Understands Your Industry
              </h2>
              <p className="text-slate-600 text-lg leading-relaxed mb-6">
                Vulcan Recruitment supports employers across construction, engineering, trades and industrial sectors with practical, responsive, and commercially focused hiring solutions.
              </p>
              <p className="text-slate-600 leading-relaxed mb-8">
                Based in Melbourne, we focus on quality over quantity – delivering better-matched candidates faster, with the industry knowledge to understand your real hiring needs.
              </p>
              <Link to="/about">
                <Button variant="outline" className="border-2 border-[#1A8B9D] text-[#1A8B9D] hover:bg-[#1A8B9D] hover:text-white font-bold uppercase tracking-wide">
                  Learn More About Us
                </Button>
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <img 
                src="https://images.unsplash.com/photo-1659353589316-d4b2ed41ad2f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwyfHxidXNpbmVzcyUyMHBlb3BsZSUyMGhhbmRzaGFrZSUyMGNvbnN0cnVjdGlvbiUyMGhhcmQlMjBoYXR8ZW58MHx8fHwxNzczNTM5MDMwfDA&ixlib=rb-4.1.0&q=85"
                alt="Business handshake"
                className="w-full h-[400px] object-cover"
              />
              <div className="absolute -bottom-6 -left-6 bg-[#1A8B9D] text-white p-6">
                <p className="font-heading text-4xl font-bold">15+</p>
                <p className="text-sm uppercase tracking-wide">Years Combined Experience</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Sectors Section */}
      <section data-testid="sectors-section" className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-[#F5A623] font-semibold uppercase tracking-wider mb-3">Our Expertise</p>
            <h2 className="font-heading text-3xl md:text-5xl font-bold text-slate-900 uppercase">
              Sectors We Serve
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {sectors.map((sector, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="group bg-slate-50 p-8 border border-slate-200 hover:border-[#1A8B9D]/50 hover:shadow-xl transition-all duration-300"
              >
                <sector.icon className="w-12 h-12 text-[#1A8B9D] mb-4 group-hover:text-[#F5A623] transition-colors" />
                <h3 className="font-heading text-xl font-bold text-slate-900 uppercase mb-2">{sector.name}</h3>
                <p className="text-slate-600 text-sm">{sector.desc}</p>
              </motion.div>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link to="/sectors">
              <Button className="bg-[#1A8B9D] hover:bg-[#115E6B] text-white font-bold uppercase tracking-wide">
                View All Sectors <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Employer Services */}
      <section data-testid="employer-services" className="py-20 md:py-28 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16">
            <div>
              <p className="text-[#F5A623] font-semibold uppercase tracking-wider mb-3">For Employers</p>
              <h2 className="font-heading text-3xl md:text-5xl font-bold uppercase mb-6">
                Hiring Solutions That Work
              </h2>
              <p className="text-slate-400 text-lg leading-relaxed mb-8">
                We help construction, engineering and industrial businesses find qualified staff without the usual hiring headaches. No time wasted on unsuitable candidates.
              </p>
              <Link to="/employers">
                <Button className="bg-[#F5A623] hover:bg-[#D98E15] text-slate-900 font-bold uppercase tracking-wide">
                  Employer Services <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {employerServices.map((service, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="bg-slate-800/50 p-6 border-l-4 border-[#1A8B9D] hover:bg-slate-800 transition-colors"
                >
                  <h4 className="font-heading text-lg font-bold uppercase mb-2">{service.title}</h4>
                  <p className="text-slate-400 text-sm">{service.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Vulcan */}
      <section data-testid="why-vulcan" className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-[#F5A623] font-semibold uppercase tracking-wider mb-3">Why Choose Us</p>
            <h2 className="font-heading text-3xl md:text-5xl font-bold text-slate-900 uppercase">
              The Vulcan Difference
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {whyVulcan.map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="text-center p-8"
              >
                <div className="w-16 h-16 bg-teal-50 rounded-full flex items-center justify-center mx-auto mb-6">
                  <item.icon className="w-8 h-8 text-[#1A8B9D]" />
                </div>
                <h3 className="font-heading text-xl font-bold text-slate-900 uppercase mb-3">{item.title}</h3>
                <p className="text-slate-600">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section data-testid="process-section" className="py-20 md:py-28 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-[#F5A623] font-semibold uppercase tracking-wider mb-3">Our Process</p>
            <h2 className="font-heading text-3xl md:text-5xl font-bold text-slate-900 uppercase">
              How We Work
            </h2>
          </div>
          <div className="grid md:grid-cols-4 gap-8">
            {processSteps.map((step, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.15 }}
                className="relative"
              >
                <div className="text-[#1A8B9D]/10 font-heading text-8xl font-bold absolute -top-4 left-0">
                  {step.num}
                </div>
                <div className="relative pt-16">
                  <h3 className="font-heading text-2xl font-bold text-slate-900 uppercase mb-3">{step.title}</h3>
                  <p className="text-slate-600">{step.desc}</p>
                </div>
                {idx < processSteps.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 right-0 transform translate-x-1/2">
                    <ArrowRight className="w-6 h-6 text-slate-300" />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Candidate CTA */}
      <section data-testid="candidate-cta" className="py-20 md:py-28 bg-[#1A8B9D]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-teal-200 font-semibold uppercase tracking-wider mb-3">For Candidates</p>
              <h2 className="font-heading text-3xl md:text-5xl font-bold text-white uppercase mb-6">
                Looking For Your Next Role?
              </h2>
              <p className="text-teal-100 text-lg leading-relaxed mb-8">
                Vulcan Recruitment works with professionals across construction, engineering, trades and industrial sectors. Register your interest and let us connect you with leading employers across Australia.
              </p>
              <Link to="/candidates">
                <Button className="bg-[#F5A623] hover:bg-[#D98E15] text-slate-900 font-bold py-6 px-8 uppercase tracking-wide text-base">
                  Submit Your CV <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white/10 backdrop-blur p-6 text-center">
                <FileText className="w-8 h-8 text-[#F5A623] mx-auto mb-3" />
                <p className="text-white font-semibold">Register Interest</p>
              </div>
              <div className="bg-white/10 backdrop-blur p-6 text-center">
                <Search className="w-8 h-8 text-[#F5A623] mx-auto mb-3" />
                <p className="text-white font-semibold">Job Matching</p>
              </div>
              <div className="bg-white/10 backdrop-blur p-6 text-center">
                <Users className="w-8 h-8 text-[#F5A623] mx-auto mb-3" />
                <p className="text-white font-semibold">Career Support</p>
              </div>
              <div className="bg-white/10 backdrop-blur p-6 text-center">
                <UserCheck className="w-8 h-8 text-[#F5A623] mx-auto mb-3" />
                <p className="text-white font-semibold">Placement Success</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section data-testid="testimonials-section" className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-[#F5A623] font-semibold uppercase tracking-wider mb-3">Testimonials</p>
            <h2 className="font-heading text-3xl md:text-5xl font-bold text-slate-900 uppercase">
              What Clients Say
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="bg-slate-50 p-8 border border-slate-100 relative"
              >
                <div className="text-[#1A8B9D]/20 text-6xl font-serif absolute top-4 left-6">"</div>
                <p className="text-slate-700 italic mb-6 pt-8 relative z-10">{testimonial.quote}</p>
                <div className="border-t border-slate-200 pt-4">
                  <p className="font-semibold text-slate-900">{testimonial.name}</p>
                  <p className="text-slate-500 text-sm">{testimonial.role}, {testimonial.company}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section data-testid="final-cta" className="py-20 md:py-28 bg-slate-900">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-3xl md:text-5xl font-bold text-white uppercase mb-6">
              Need Skilled People For Your Next Project?
            </h2>
            <p className="text-slate-400 text-lg mb-10">
              Let's talk about your hiring needs. Our team is ready to help you find the right people, fast.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/employers#request-staff">
                <Button 
                  data-testid="final-request-staff"
                  className="bg-[#1A8B9D] hover:bg-[#115E6B] text-white font-bold py-6 px-10 uppercase tracking-wide text-base"
                >
                  Request Staff
                </Button>
              </Link>
              <Link to="/contact">
                <Button 
                  data-testid="final-contact"
                  className="bg-[#F5A623] hover:bg-[#D98E15] text-slate-900 font-bold py-6 px-10 uppercase tracking-wide text-base"
                >
                  Contact Us
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
